import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { billingApi } from '@/lib/api';

interface PaymentFailedModalProps {
  businessId: string;
  onClose: () => void;
}

const animationConfig = {
  backdrop: { bg: 'rgba(0, 0, 0, 0.04)', blur: '8px' },
  duration: 400,
};

export function PaymentFailedModal({ businessId }: PaymentFailedModalProps) {
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'hidden'>('hidden');
  const config = animationConfig;

  useEffect(() => {
    setPhase('entering');
    const timer = setTimeout(() => setPhase('visible'), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleUpdatePayment = async () => {
    setLoading(true);
    const response = await billingApi.createPortalSession(businessId);
    if (response.data?.portal_url) {
      window.location.href = response.data.portal_url;
    } else {
      setLoading(false);
    }
  };

  const showContent = phase !== 'hidden';
  if (!showContent && phase === 'hidden') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - not clickable */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: phase === 'visible' ? config.backdrop.bg : 'rgba(0, 0, 0, 0)',
          backdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          WebkitBackdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          transition: `background-color ${config.duration}ms ease-out, backdrop-filter ${config.duration}ms ease-out`,
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl"
        style={{
          opacity: phase === 'visible' ? 1 : 0,
          transform: phase === 'visible' ? 'scale(1)' : 'scale(0.98)',
          transition: `opacity ${config.duration}ms ease-out, transform ${config.duration}ms ease-out`,
        }}
      >
        <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden">
          <div className="grid md:grid-cols-2">
            {/* Left side - Failed card illustration */}
            <div className="bg-zinc-50 dark:bg-zinc-800/50 p-6 md:p-8 flex items-center justify-center">
              <div className="w-full max-w-[280px]">
                {/* Card mockup */}
                <div className="rounded-2xl bg-gradient-to-br from-zinc-800 to-zinc-900 dark:from-zinc-700 dark:to-zinc-800 p-6 shadow-xl relative overflow-hidden">
                  {/* Card chip */}
                  <div className="w-10 h-7 rounded-md bg-amber-400/80 mb-8" />
                  {/* Card number */}
                  <div className="flex gap-3 mb-6">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                    </div>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                    </div>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                      <div className="w-2 h-2 rounded-full bg-zinc-500" />
                    </div>
                    <span className="text-sm text-zinc-400 font-mono">4242</span>
                  </div>
                  {/* Card details */}
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">Expires</div>
                      <div className="text-sm text-red-400 font-mono">EXP</div>
                    </div>
                    {/* Generic card network icon */}
                    <div className="w-8 h-6 rounded bg-zinc-600/50 flex items-center justify-center">
                      <svg className="w-4 h-4 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Status line below card */}
                <div className="mt-4 flex items-center justify-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">Charge failed</span>
                </div>
              </div>
            </div>

            {/* Right side - Action */}
            <div className="px-8 py-8 md:py-10 flex flex-col justify-center">
              <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                Payment method failed
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
                We couldn't charge your card for your subscription renewal. Update your payment method to avoid losing access.
              </p>

              {/* Info box */}
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl px-4 py-3 mb-6">
                <p className="text-sm text-amber-900 dark:text-amber-200">
                  Update your card now to keep your account active. It only takes a moment.
                </p>
              </div>

              {/* CTA */}
              <Button
                className="w-full h-11"
                onClick={handleUpdatePayment}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Update payment method'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
